/**
 * The clinical picture, at desktop width, on the doctor's patient page.
 *
 * Source: Brian, 18 September 2026, "optimize the doctor's portal for
 * desktop", and the desktop dashboard visual of 15 September: a hero band, the
 * four score cards with movement since the last check, a progress chart with
 * marker tabs, recent lab highlights, and an upcoming and recent panel.
 *
 * Worth recording where that visual actually belongs, because it is headed
 * "Alex Parker, DO" and shows one person's own health data. A clinician's
 * dashboard cannot show one patient's weight, and his own documents say the
 * clinic dashboard is a work queue, with a separate visual showing exactly
 * that. We read the header as pasted on by mistake and the content as a
 * patient's clinical picture, so it lives where a doctor looks at one patient:
 * the patient page.
 *
 * It renders from `/patients/:id/overview`, which is the same assembler the
 * patient's own app uses. One source, so a doctor and a patient can never be
 * shown different numbers for the same blood draw.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api';
import { Card, LoadingRows } from './ui';

interface Ring {
  key: string;
  label: string;
  score: number | null;
  movement: number | null;
}

interface SeriesPoint {
  at: string;
  value: number;
}

interface ProgressSeries {
  key: string;
  label: string;
  unit: string;
  points: SeriesPoint[];
  current: number | null;
  changeSinceStart: number | null;
  lowerIsBetter: boolean;
}

interface LabHighlight {
  markerId: string;
  label: string;
  value: number;
  unit: string;
  delta: number | null;
  lowerIsBetter: boolean;
  bandLabel: string | null;
}

interface TimelineEntry {
  kind: string;
  title: string;
  subtitle: string;
  at: string;
  upcoming: boolean;
}

interface Overview {
  firstName: string;
  rings: Ring[];
  healthAge: {
    healthAge: number | null;
    chronologicalAge: number | null;
    differenceLabel: string | null;
    message: string | null;
  } | null;
  progress: ProgressSeries[];
  labHighlights: LabHighlight[];
  timeline: TimelineEntry[];
  latestReportId: string | null;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function bandColor(score: number | null): string {
  if (score === null) return 'rgba(27,36,48,0.15)';
  if (score >= 80) return 'var(--band-optimal)';
  if (score >= 60) return 'var(--band-warn)';
  return 'var(--band-risk)';
}

/**
 * One score as a closed ring beside its label, the card shape from the visual.
 *
 * The band colours are engine semantics shared with the printed PDF and are
 * never restyled: a green ring here and a green dial on the report mean the
 * same thing, which is the only reason either is readable at a glance.
 */
function ScoreCard({ ring }: { ring: Ring }) {
  const size = 56;
  const stroke = 6;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const value = Math.min(100, Math.max(0, ring.score ?? 0));

  return (
    <div className="glass-standard flex items-center gap-4 rounded-md2 border border-[var(--hairline)] p-4">
      <svg width={size} height={size} className="shrink-0" role="img" aria-label={`${ring.label}, ${ring.score ?? 'not scored'}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(27,36,48,0.09)" strokeWidth={stroke} />
        {ring.score !== null && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={bandColor(ring.score)}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - value / 100)}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )}
      </svg>
      <div className="min-w-0">
        <p className="text-xs text-ink-muted">{ring.label}</p>
        <p className="font-display text-2xl font-bold leading-none text-ink">
          {ring.score ?? '-'}
          <span className="ml-0.5 text-xs font-medium text-ink-muted">/100</span>
        </p>
        {/* Movement only where there is movement. "Up 0 since last check" is a
            sentence that tells a doctor nothing and costs them a read. */}
        {ring.movement !== null && ring.movement !== 0 && (
          <p className={`mt-0.5 text-[11px] font-semibold ${ring.movement > 0 ? 'text-sage-deep' : 'text-ink-muted'}`}>
            {ring.movement > 0 ? 'Up' : 'Down'} {Math.abs(ring.movement)} since last check
          </p>
        )}
      </div>
    </div>
  );
}

export function PatientOverview({ patientId }: { patientId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['patient-overview', patientId],
    queryFn: () => api.get<Overview>(`/patients/${patientId}/overview`),
    // A patient with no released report has no overview, which is normal and
    // not worth retrying into.
    retry: false,
  });
  const [tab, setTab] = useState<string | null>(null);

  if (isLoading) return <LoadingRows rows={4} />;
  if (!data || data.rings.length === 0) return null;

  const series = data.progress.find((s) => s.key === (tab ?? data.progress[0]?.key)) ?? null;

  return (
    <div className="mb-6 space-y-4">
      {/* The hero band, his own words, at desktop width. */}
      <div className="flex flex-wrap items-center justify-between gap-6 rounded-md2 border border-[var(--hairline)] bg-[var(--paper-deep)] px-6 py-5">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.16em] text-ink-muted">Your health journey</p>
          <p className="mt-1 font-display text-2xl font-bold leading-tight text-ink">
            Real data. A healthier tomorrow.
          </p>
        </div>
        <p className="max-w-xs font-display text-sm italic leading-relaxed text-ink-muted">
          &ldquo;Better health gives you more good days.&rdquo;
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {data.rings.map((ring) => (
          <ScoreCard key={ring.key} ring={ring} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        {series && (
          <Card title="Progress" padded>
            <div className="-mx-1 mb-3 flex gap-1.5 overflow-x-auto px-1 pb-1">
              {data.progress.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setTab(option.key)}
                  className={`shrink-0 rounded-control px-3 py-1.5 text-xs font-semibold transition ${
                    option.key === series.key
                      ? 'bg-[var(--brand-accent)] text-white'
                      : 'bg-ink/5 text-ink-muted hover:text-ink'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {series.points.length < 2 ? (
              <p className="py-10 text-center text-sm text-ink-muted">
                One reading so far. A line needs two.
              </p>
            ) : (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={series.points.map((point) => ({
                      label: formatDate(point.at),
                      value: point.value,
                    }))}
                    margin={{ top: 6, right: 6, bottom: 0, left: -18 }}
                  >
                    <defs>
                      <linearGradient id="patient-overview-progress" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--sage)" stopOpacity={0.26} />
                        <stop offset="100%" stopColor="var(--sage)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--ink-muted)' }} stroke="rgba(27,36,48,0.12)" />
                    <YAxis
                      domain={['dataMin - 4', 'dataMax + 4']}
                      tick={{ fontSize: 11, fill: 'var(--ink-muted)' }}
                      stroke="rgba(27,36,48,0.12)"
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: '1px solid var(--hairline)', background: '#fff', fontSize: 12 }}
                      formatter={(value: number) => [`${value} ${series.unit}`, series.label]}
                    />
                    <Area type="monotone" isAnimationActive={false} dataKey="value" stroke="var(--sage)" strokeWidth={2.5} fill="url(#patient-overview-progress)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="mt-3 flex items-end justify-between border-t border-ink/5 pt-3">
              <div>
                <p className="text-xs text-ink-muted">Current {series.label.toLowerCase()}</p>
                <p className="font-display text-xl font-bold text-ink">
                  {series.current ?? '-'}
                  <span className="ml-1 text-xs font-medium text-ink-muted">{series.unit}</span>
                </p>
              </div>
              {series.changeSinceStart !== null && series.changeSinceStart !== 0 && (
                <p
                  className={`text-sm font-semibold ${
                    series.changeSinceStart < 0 === series.lowerIsBetter ? 'text-sage-deep' : 'text-ink-muted'
                  }`}
                >
                  {series.changeSinceStart < 0 ? 'Down' : 'Up'} {Math.abs(series.changeSinceStart)} {series.unit}
                  <span className="ml-1 font-normal text-ink-muted">since starting</span>
                </p>
              )}
            </div>
          </Card>
        )}

        {data.timeline.length > 0 && (
          <Card title="Upcoming and recent" padded>
            <ul className="divide-y divide-ink/5">
              {data.timeline.map((entry, index) => (
                <li key={`${entry.kind}-${index}`} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <span
                    aria-hidden="true"
                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                      entry.upcoming ? 'bg-[var(--brand-accent)]' : 'bg-ink/15'
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-ink">{entry.title}</span>
                    <span className="block text-xs text-ink-muted">{entry.subtitle}</span>
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-xs text-ink-muted">
                    {entry.upcoming ? 'Due ' : ''}
                    {formatDate(entry.at)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {data.labHighlights.length > 0 && (
        <Card
          title="Recent lab highlights"
          action={
            data.latestReportId ? (
              <Link
                to={`/reports/${data.latestReportId}`}
                className="text-xs font-medium text-[var(--brand-accent)] hover:underline"
              >
                See all
              </Link>
            ) : undefined
          }
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
            {data.labHighlights.map((highlight) => (
              <div key={highlight.markerId} className="min-w-0">
                <p className="text-xs text-ink-muted">{highlight.label}</p>
                <p className="font-display text-2xl font-bold leading-tight text-ink">
                  {highlight.value}
                  <span className="ml-1 text-xs font-medium text-ink-muted">{highlight.unit}</span>
                </p>
                {highlight.delta !== null && highlight.delta !== 0 && (
                  <p
                    className={`text-xs font-semibold ${
                      highlight.delta < 0 === highlight.lowerIsBetter ? 'text-sage-deep' : 'text-ink-muted'
                    }`}
                  >
                    {highlight.delta < 0 ? 'Down' : 'Up'} {Math.abs(highlight.delta)} since last draw
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {data.healthAge?.healthAge !== null && data.healthAge && (
        <Card title="Health age">
          <div className="flex flex-wrap items-end gap-6">
            <p className="font-display text-3xl font-bold leading-none text-ink">
              {data.healthAge.healthAge}
              <span className="ml-1 text-sm font-medium text-ink-muted">years</span>
            </p>
            {data.healthAge.chronologicalAge !== null && (
              <p className="pb-1 text-sm text-ink-muted">
                Chronological age {data.healthAge.chronologicalAge}
                {data.healthAge.differenceLabel ? ` · ${data.healthAge.differenceLabel}` : ''}
              </p>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
